/* eslint-disable no-undef */
import { unwrapNodes } from "@/helpers/graphql";
import * as Sentry from "@sentry/vue";
import { trackPolarAddToCart } from "@/helpers/polar";

/**
 * Runs a third-party tracking call so that it can never throw into the
 * calling flow. Vendor globals (fbq, _learnq, VWO, nostojs...) may be missing
 * when the customer has not given cookie consent or an ad blocker is active.
 */
const safeTrack = (label, fn) => {
  try {
    fn();
  } catch (err) {
    console.warn(`[Tracking] ${label} failed:`, err);
  }
};

/**
 * Publishes a product_added_to_cart event to Shopify's Web Pixels system.
 *
 * This bridges our custom Storefront API cart flow with Shopify's pixel
 * infrastructure so all App Pixels (Polar, Meta, Klaviyo, etc.) receive
 * add-to-cart tracking data.
 *
 * Call this AFTER a successful cart addition response — never optimistically.
 *
 * NOTE: Shopify blocks standard event names from being published by theme-side JS,
 * so we use a custom event name 'ratandboa:product_added_to_cart'. Pixel providers
 * (Polar, Meta, Klaviyo) need to subscribe to 'all_custom_events' or be configured
 * to listen for this specific custom event name.
 *
 * @param {Object} params
 * @param {string} params.variantId       - Shopify variant GID, e.g. "gid://shopify/ProductVariant/12345"
 * @param {string} params.productId       - Shopify product GID, e.g. "gid://shopify/Product/12345"
 * @param {string} params.productTitle    - Product title
 * @param {string} params.productType     - Product type (e.g. "Dresses")
 * @param {string} params.productUrl      - Full URL or relative URL, e.g. "/products/some-dress"
 * @param {string} params.variantTitle    - Variant title (e.g. "UK 8" or "S / Black")
 * @param {string} params.sku             - Variant SKU
 * @param {number} params.unitPrice       - Price per unit as a number (e.g. 180.00)
 * @param {string} params.currencyCode    - ISO currency code (e.g. "GBP")
 * @param {number} params.quantity        - Quantity added
 * @param {string} [params.imageUrl]      - Product/variant image URL (optional)
 * @param {string} [params.vendor]        - Vendor name (optional, defaults to "Rat & Boa")
 */
export const publishShopifyAddToCartEvent = ({
  variantId,
  productId,
  productTitle,
  productType,
  productUrl,
  variantTitle,
  sku,
  unitPrice,
  currencyCode,
  quantity,
  imageUrl,
  vendor = "Rat & Boa",
}) => {
  // Guard: Shopify.analytics may not be available in all contexts
  if (typeof Shopify === "undefined" || !Shopify.analytics?.publish) {
    console.warn("[Analytics] Shopify.analytics.publish not available");
    return;
  }

  // Ensure unitPrice is a number
  const price = parseFloat(unitPrice) || 0;
  const totalAmount = price * quantity;

  // Extract relative URL if full URL is provided
  let relativeUrl = productUrl;
  if (productUrl && productUrl.startsWith("http")) {
    try {
      relativeUrl = new URL(productUrl).pathname;
    } catch (e) {
      relativeUrl = productUrl;
    }
  }

  const payload = {
    cartLine: {
      cost: {
        totalAmount: {
          amount: totalAmount,
          currencyCode: currencyCode,
        },
      },
      merchandise: {
        id: variantId,
        image: imageUrl ? { src: imageUrl } : undefined,
        price: {
          amount: price,
          currencyCode: currencyCode,
        },
        product: {
          id: productId,
          title: productTitle,
          type: productType || "",
          untranslatedTitle: productTitle,
          url: relativeUrl,
          vendor: vendor,
        },
        sku: sku || "",
        title: variantTitle,
        untranslatedTitle: variantTitle,
      },
      quantity: quantity,
    },
  };

  try {
    // Using custom event name because Shopify blocks standard event names
    // from being published by theme-side JS. Coordinate with Polar/Meta/Klaviyo
    // to ensure they subscribe to this custom event via all_custom_events.
    Shopify.analytics.publish("ratandboa:product_added_to_cart", payload);
    console.log(
      "[Analytics] Shopify ratandboa:product_added_to_cart event published"
    );
  } catch (err) {
    console.warn("[Analytics] Failed to publish add-to-cart event:", err);
    Sentry.captureException(err);
  }
};

export const addToCartEvent = async ({ sku, quantity, cart }) => {
  const cartLines = unwrapNodes(cart.lines.edges);

  updateCartEvent(cart).catch((err) =>
    console.warn("[Tracking] Nosto cart update failed:", err)
  );

  const { merchandise } = cartLines.find((line) => line.merchandise.id === sku);
  const merchandiseId = merchandise.product.id;
  const productId = merchandiseId.split("Product/")[1];

  const eventId = "RAB_" + new Date().getTime() + "_" + sku;

  const body = JSON.stringify({
    sku: productId,
    quantity,
    url: merchandise.product.onlineStoreUrl,
    price: merchandise.priceV2.amount,
    currency: merchandise.priceV2.currencyCode,
    eventId,
  });

  // The Meta Pixel is only loaded once marketing consent is given, so fbq is
  // undefined on consent-gated stores (e.g. EU) until the banner is accepted.
  safeTrack("Meta AddToCart", () => {
    if (typeof fbq !== "function") return;
    fbq(
      "track",
      "AddToCart",
      {
        content_type: "product_group",
        content_ids: [productId],
        value: merchandise.priceV2.amount,
        num_items: quantity,
        content_name: merchandise.product.title,
        currency: merchandise.priceV2.currencyCode,
      },
      {
        eventID: eventId,
      }
    );
  });

  const imageUrl = unwrapNodes(merchandise.product.images?.edges)?.[0]?.klaviyo;

  const collections = unwrapNodes(merchandise.product.collections.edges);
  const collectionTitles = collections.map((collection) => collection.title);

  const item = {
    Name: merchandise.product.title,
    ProductID: productId,
    Categories: collectionTitles,
    ImageURL: imageUrl,
    URL: merchandise.product.onlineStoreUrl,
    Brand: merchandise.product.vendor,
    Price: merchandise.priceV2.amount,
    CompareAtPrice:
      merchandise.product.compareAtPriceRange.minVariantPrice.amount,
  };

  safeTrack("Klaviyo Added to Cart", () => {
    if (typeof _learnq === "undefined") return;
    _learnq.push(["track", "Added to Cart", item]);
  });

  const addToCart = (merchandise, productId) => {
    const item = {
      item_name:
        merchandise.product.title +
        ` - ` +
        merchandise.selectedOptions[0].value,
      item_id:
        `shopify_US_` +
        productId +
        `_` +
        merchandise.id.split("ProductVariant/")[1],
      price: merchandise.priceV2.amount,
      item_brand: merchandise.product.vendor,
      item_category: merchandise.product.productType,
      item_variant: merchandise.selectedOptions[0].value,
      quantity: "1",
    };

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "add_to_cart",
      ecommerce: {
        items: [item],
      },
    });
  };

  safeTrack("GA4 add_to_cart", () => addToCart(merchandise, productId));

  safeTrack("VWO addedToCart", () => {
    window.VWO = window.VWO || [];
    VWO.event =
      VWO.event ||
      function () {
        VWO.push(["event"].concat([].slice.call(arguments)));
      };
    VWO.event("addedToCart", {
      added_to_cart: true,
    });
  });

  // Publish to Shopify Web Pixels for App Pixel integrations (Polar, Meta, Klaviyo, etc.)
  const variantTitle =
    merchandise.selectedOptions?.map((opt) => opt.value).join(" / ") || "";

  safeTrack("Shopify pixel add to cart", () =>
    publishShopifyAddToCartEvent({
      variantId: merchandise.id,
      productId: merchandise.product.id,
      productTitle: merchandise.product.title,
      productType: merchandise.product.productType,
      productUrl: merchandise.product.onlineStoreUrl,
      variantTitle: variantTitle,
      sku: merchandise.sku || "",
      unitPrice: parseFloat(merchandise.priceV2.amount),
      currencyCode: merchandise.priceV2.currencyCode,
      quantity: quantity,
      imageUrl: imageUrl,
      vendor: merchandise.product.vendor,
    })
  );

  try {
    await trackPolarAddToCart({
      variantId: merchandise.id,
      productId: merchandise.product.id,
      productTitle: merchandise.product.title,
      productType: merchandise.product.productType,
      productUrl: merchandise.product.onlineStoreUrl,
      variantTitle: variantTitle,
      sku: merchandise.sku || "",
      unitPrice: parseFloat(merchandise.priceV2.amount),
      currencyCode: merchandise.priceV2.currencyCode,
      quantity: quantity,
      imageUrl: imageUrl,
      vendor: merchandise.product.vendor,
    });
  } catch (err) {
    console.log("[Polar Analytics] Add to Cart tracking error:", err);
  }

  await fetch(`${process.env.RATANDBOA_NODE_APP_URL}/event`, {
    method: "post",
    headers: { "Content-Type": "application/json" },
    body,
  });
};

export const updateCartEvent = async (cart) => {
  const cartLines = unwrapNodes(cart.lines.edges);

  const items = [];
  cartLines.forEach((line) => {
    const productId = line.merchandise.product.id;
    const merchandiseId = line.merchandise.id;
    items.push({
      name: line.merchandise.product.title,
      price_currency_code: line.merchandise.priceV2.currencyCode,
      product_id: productId.split("Product/")[1],
      quantity: 1,
      sku_id: merchandiseId.split("ProductVariant/")[1],
      unit_price: line.merchandise.priceV2.amount,
    });
  });

  // eslint-disable-next-line no-undef
  nostojs((api) => {
    api.defaultSession().setCart({
      items,
    });
  });
};

export const viewFrontPageEvent = async () => {
  // eslint-disable-next-line no-undef
  nostojs((api) => {
    api
      .defaultSession()
      .viewFrontPage()
      .setPlacements(api.placements.getPlacements())
      .load()
      .then((data) => {
        console.log(data.recommendations);
      });
  });
};

export const viewProductEvent = async (product) => {
  const productId = product.id.split("Product/")[1];
  const imageUrl = unwrapNodes(product.images.edges)[0].largeMedia;

  const collections = unwrapNodes(product.collections.edges);
  const collectionTitles = collections.map((collection) => collection.title);

  const firstVariantPrice = unwrapNodes(product.variants.edges)[0].priceV2
    .amount;

  const item = {
    Name: product.title,
    ProductID: productId,
    Categories: collectionTitles,
    ImageURL: imageUrl,
    URL: product.onlineStoreUrl,
    Brand: product.vendor,
    Price: firstVariantPrice,
    CompareAtPrice: product.compareAtPriceRange.minVariantPrice.amount,
  };

  _learnq.push(["track", "Viewed Product", item]);
  _learnq.push([
    "trackViewedItem",
    {
      Title: item.Name,
      ItemId: item.ProductID,
      Categories: item.Categories,
      ImageUrl: item.ImageURL,
      Url: item.URL,
      Metadata: {
        Brand: item.Brand,
        Price: item.Price,
        CompareAtPrice: item.CompareAtPrice,
      },
    },
  ]);
};

// Nosto expects the collection *title* (not the handle) as the category name;
// the numeric collection id is attached when the session API supports it so
// Category Merchandising 2.0 analytics can match the page to its category.
export const viewCollectionEvent = async (collectionTitle, collectionId) => {
  Sentry.addBreadcrumb({
    category: "events",
    message: "viewCollectionEvent",
    data: {
      collectionTitle,
      collectionId,
    },
    level: "info",
  });

  nostojs((api) => {
    const action = api.defaultSession().viewCategory(collectionTitle);

    if (collectionId && typeof action.setCategoryIds === "function") {
      action.setCategoryIds([String(collectionId)]);
    }

    action
      .setPlacements(api.placements.getPlacements())
      .load()
      .then((data) => {
        console.log(data.recommendations);
      })
      .catch((error) => {
        console.log(error);
        Sentry.captureException(error);
      });
  });
};

export const searchEvent = async (searchQuery) => {
  console.log("nostoSearchQuery", searchQuery);
  // eslint-disable-next-line no-undef
  nostojs((api) => {
    api
      .defaultSession()
      .viewSearch(searchQuery)
      .setPlacements(["searchpage-nosto-1", "searchpage-nosto-2"])
      .load()
      .then((data) => {
        console.log(data.recommendations);
      });
  });
};

export const view404Event = async () => {
  // eslint-disable-next-line no-undef
  nostojs((api) => {
    api
      .defaultSession()
      .viewNotFound()
      .setPlacements(api.placements.getPlacements())
      .load()
      .then((data) => {
        console.log(data.recommendations);
      });
  });
};

export const viewGeneralPageEvent = async () => {
  // eslint-disable-next-line no-undef
  nostojs((api) => {
    api
      .defaultSession()
      .viewOther()
      .setPlacements(api.placements.getPlacements())
      .load()
      .then((data) => {
        console.log(data.recommendations);
      });
  });
};

export const trackDataLayerEvent = (eventData) => {
  try {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(eventData);
  } catch (e) {
    // Silently fail -- tracking must never break user flows
  }
};

export const registerEvent = async ({ method, email, loyaltyListId }) => {
  trackDataLayerEvent({
    event: "sign_up",
    method,
  });

  if (typeof fbq === "function") {
    fbq("track", "Complete Registration Success");
  }

  try {
    const utmDetails = localStorage.getItem("shopifyUTMDetails");
    let parsedUtmDetails;

    if (utmDetails) {
      parsedUtmDetails = JSON.parse(utmDetails);
    }

    const body = {
      email,
      ...(parsedUtmDetails?.utmCampaign
        ? { utmCampaign: parsedUtmDetails.utmCampaign }
        : {}),
      ...(parsedUtmDetails?.utmSource
        ? { utmSource: parsedUtmDetails.utmSource }
        : {}),
      ...(loyaltyListId && { loyaltyListId }),
    };

    await fetch(process.env.RATANDBOA_NODE_APP_URL + "/event/register", {
      method: "post",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
  } catch (error) {
    console.log(error);
    Sentry.captureException(error);
  }
};

export const smsCaptureEvent = ({ location }) => {
  window.dataLayer.push({
    event: "sms_capture",
    location,
  });
};

export const birthdayCaptureEvent = ({ location }) => {
  window.dataLayer.push({
    event: "birthday_capture",
    location,
  });
};

export const loginEvent = ({ method }) => {
  window.dataLayer.push({
    event: "login",
    method,
  });
};

export const addToWishlistEvent = () => {
  window.dataLayer.push({
    event: "add_to_wishlist",
  });
};

/**
 * Send an AB Tasty "Action Tracking" event. AB Tasty attributes it to the
 * visitor's campaign variation automatically; reporting requires an Action
 * Tracking goal with the same name in the AB Tasty dashboard.
 *
 * The AB Tasty tag loads asynchronously (Shopify app embed): if it isn't
 * ready yet, the event is deferred until the tag dispatches
 * "abtasty_trackingInitialized". Never throws — tracking must not break
 * user flows (e.g. when AB Tasty is ad-blocked or absent in Storybook).
 *
 * @param {string} action - Event name, shown as "Event Action" in AB Tasty
 * @param {Object} [options]
 * @param {string} [options.label] - Optional "Event Label" for segmenting
 * @param {number} [options.value] - Optional value (positive integer)
 */
export const abTastyEvent = (action, { label, value } = {}) => {
  try {
    const send = () => {
      try {
        window.abtasty.send("event", {
          ec: "Action Tracking",
          ea: action,
          ...(label && { el: label }),
          ...(value && { ev: value }),
        });
      } catch (e) {
        // Silently fail -- tracking must never break user flows
      }
    };

    if (
      window.ABTasty?.eventState?.trackingInitialized?.status === "complete"
    ) {
      send();
      return;
    }

    window.addEventListener("abtasty_trackingInitialized", send, {
      once: true,
    });
  } catch (e) {
    // Silently fail -- tracking must never break user flows
  }
};

export const bisEvent = ({ waitlistVersion } = {}) => {
  trackDataLayerEvent({
    event: "bis_signup",
    // Identifies the PDP waitlist A/B test arm (0 = control, 1 = button
    // click, 2 = auto-open) so signups can be split per variant.
    ...(waitlistVersion != null && { waitlist_version: waitlistVersion }),
  });

  window.VWO = window.VWO || [];
  VWO.event =
    VWO.event ||
    function () {
      VWO.push(["event"].concat([].slice.call(arguments)));
    };
  VWO.event("bis_signup", {
    added_to_cart: true,
  });

  abTastyEvent("bis_signup", {
    label:
      waitlistVersion != null ? `waitlistVersion:${waitlistVersion}` : null,
  });
};

export const heroClickEvent = ({ title, link }) => {
  window.dataLayer.push({
    event: "hero_click",
    hero_title: title,
    hero_link: link,
  });
};

export const navigationClickEvent = ({ title, url }) => {
  console.log("navigationClickEvent", title, url);
  window.dataLayer.push({
    event: "navigation_click",
    nav_title: title,
    nav_url: url,
  });
};

export const childNavigationClickEvent = ({ title, url }) => {
  window.dataLayer.push({
    event: "child_navigation_click",
    child_nav_title: title,
    child_nav_url: url,
  });
};

export const productPageViewEvent = ({ eventName, urlPattern }) => {
  window.dataLayer.push({
    event: eventName,
    page_url: window.location.href,
    url_pattern: urlPattern,
  });
};

export const productClickEvent = ({ product, position, listName }) => {
  const productId = product.id.split("Product/")[1];
  const firstVariantPrice =
    unwrapNodes(product.variants.edges)[0]?.priceV2?.amount || "0";

  const collections = unwrapNodes(product.collections?.edges || []);
  const collectionTitles = collections.map((collection) => collection.title);

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: "product_click",
    ecommerce: {
      items: [
        {
          item_name: product.title,
          item_id: `shopify_US_${productId}`,
          price: firstVariantPrice,
          item_brand: product.vendor,
          item_category: product.productType,
          item_list_name: listName || "Product Listing Page",
          index: position,
        },
      ],
    },
    product_id: productId,
    product_title: product.title,
    product_price: firstVariantPrice,
    product_url: product.onlineStoreUrl,
    product_categories: collectionTitles,
  });
};

export const checkProductPages = () => {
  const productPatterns = [
    {
      urlPattern: "fiorella-dress",
      eventName: "fl_test_page_view",
    },
    {
      urlPattern: "antheia-dress",
      eventName: "fl_test_page_view",
    },
    {
      urlPattern: "lorela-dress",
      eventName: "fl_test_page_view",
    },
    {
      urlPattern: "magnolia-floral-dress",
      eventName: "fl_test_page_view",
    },
    {
      urlPattern: "valentina-dress",
      eventName: "fl_test_page_view",
    },
    {
      urlPattern: "cecelia-dress",
      eventName: "fl_test_page_view",
    },
    {
      urlPattern: "santino-dress",
      eventName: "fl_test_page_view",
    },
    {
      urlPattern: "olympia-sequin-dress",
      eventName: "fl_test_page_view",
    },
    {
      urlPattern: "athena-dress",
      eventName: "fl_test_page_view",
    },
    {
      urlPattern: "casablanca-dress",
      eventName: "fl_test_page_view",
    },
    {
      urlPattern: "catarina-dress",
      eventName: "fl_test_page_view",
    },
    {
      urlPattern: "adriana-dress",
      eventName: "fl_test_page_view",
    },
    {
      urlPattern: "selena-dress",
      eventName: "fl_test_page_view",
    },
    {
      urlPattern: "luciana-dress",
      eventName: "fl_test_page_view",
    },
    {
      urlPattern: "asteria-dress",
      eventName: "fl_test_page_view",
    },
    {
      urlPattern: "rafinha-dress",
      eventName: "fl_test_page_view",
    },
  ];

  const currentUrl = window.location.href;

  productPatterns.forEach(({ urlPattern, eventName }) => {
    if (currentUrl.includes(urlPattern)) {
      productPageViewEvent({ eventName, urlPattern });
    }
  });
};
